import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Clientes }     from './Clientes';
import { Usuarios }     from './Usuarios';
import { PerfisAcesso, MODULOS_SISTEMA } from './PerfisAcesso';
import { supabase }     from '../lib/supabase';
import { toast }        from '../utils/toast';
import { criarAcessoNoServidor, avisarUsuariosAlterados } from '../utils/criarAcesso';

const LS_ALUNOS = 'kzerodiary_alunos_cadastro';
const salvarLS  = (v) => { try { localStorage.setItem(LS_ALUNOS, JSON.stringify(v)); } catch { /* quota */ } };

const FORM_VAZIO = { nome:'', email:'', senha:'', perfil_acesso_id:'', empresa:'' };

// Resumo legível das permissões de um perfil
function ResumoPermissoes({ perfil }) {
  const [aberto, setAberto] = useState(false);
  if (!perfil) return <span style={{ fontSize:12, color:'#64748b' }}>Sem perfil de acesso definido — acesso padrão de aluno.</span>;

  const perm = perfil.permissoes || {};
  const modulosVisiveis = MODULOS_SISTEMA.filter(m => perm[m.id]?.ver !== false);
  const modulosBloqueados = MODULOS_SISTEMA.filter(m => perm[m.id]?.ver === false);

  return (
    <div style={{ marginTop:'0.5rem' }}>
      <button
        style={{ background:'none', border:'none', color:'#8b5cf6', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:4, padding:0 }}
        onClick={() => setAberto(!aberto)}
      >
        <Shield size={12}/> Ver permissões de "{perfil.nome}" {aberto ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
      </button>

      {aberto && (
        <div style={{ marginTop:'0.5rem', background:'#ffffff', border:'1px solid #eeeaf5', borderRadius:8, padding:'0.75rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {/* Módulos com acesso */}
          <div>
            <div style={{ fontSize:11, color:'#10b981', fontWeight:600, marginBottom:'0.375rem' }}>✓ MÓDULOS COM ACESSO</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
              {modulosVisiveis.length === 0
                ? <span style={{ fontSize:12, color:'#64748b' }}>Nenhum módulo liberado.</span>
                : modulosVisiveis.map(m => (
                    <span key={m.id} style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.25)' }}>
                      {m.emoji} {m.label}
                    </span>
                  ))
              }
            </div>
          </div>

          {/* Módulos bloqueados */}
          {modulosBloqueados.length > 0 && (
            <div>
              <div style={{ fontSize:11, color:'#ef4444', fontWeight:600, marginBottom:'0.375rem' }}>✗ MÓDULOS BLOQUEADOS</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {modulosBloqueados.map(m => (
                  <span key={m.id} style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'rgba(239,68,68,0.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)' }}>
                    {m.emoji} {m.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Restrições dentro dos módulos */}
          {modulosVisiveis.map(m => {
            const restricoes = [];
            if (perm[m.id]?.criar === false)       restricoes.push('não pode criar');
            if (perm[m.id]?.editarTodos === false)  restricoes.push('não pode editar');
            if (perm[m.id]?.excluirTodos === false) restricoes.push('não pode excluir');
            if (!restricoes.length) return null;
            return (
              <div key={m.id} style={{ fontSize:11, color:'#f59e0b' }}>
                {m.emoji} <strong>{m.label}:</strong> {restricoes.join(', ')}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AbaAlunos({ perfisAcesso = [], clientes = [] }) {
  // Cliente do aluno: grava o nome em perfis.empresa, que o relatório de
  // trilhas já usa para filtrar por empresa (16/10/2026).
  const nomesClientes = [...new Set((clientes || []).map(c => (c?.nome || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const [alunos, setAlunos]         = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm]             = useState(null);
  const [erro, setErro]             = useState('');
  const [salvando, setSalvando]     = useState(false);

  const carregar = async () => {
    setCarregando(true);
    const { data } = await supabase.from('perfis').select('*').eq('papel', 'aluno').order('criado_em');
    const lista = data || [];
    setAlunos(lista);
    salvarLS(lista);
    setCarregando(false);
  };

  useEffect(() => { carregar(); }, []);

  const salvar = async () => {
    if (!form.nome || !form.email) { setErro('Nome e e-mail são obrigatórios.'); return; }
    if (!form.empresa) { setErro('Selecione o cliente do aluno.'); return; }
    if (!form.id && !form.senha)   { setErro('Informe uma senha para o novo aluno.'); return; }
    setErro('');
    setSalvando(true);

    const perfilAcessoId = form.perfil_acesso_id || null;

    if (form.id) {
      const { error } = await supabase.from('perfis').update({
        nome:             form.nome,
        email:            form.email,
        perfil_acesso_id: perfilAcessoId,
        empresa:          form.empresa || null,
      }).eq('id', form.id);
      if (error) { setErro('Erro ao atualizar: ' + error.message); setSalvando(false); return; }
      toast.sucesso('Aluno atualizado!');
      avisarUsuariosAlterados();
    } else {
      // Cadastro pelo servidor (api/criar-acesso): conta já confirmada, sem o
      // e-mail em inglês do Supabase, e boas-vindas da Kzero (16/10/2026).
      const r = await criarAcessoNoServidor({
        nome: form.nome,
        email: form.email,
        senha: form.senha,
        papel: 'aluno',
        empresa: form.empresa || null,
        perfil_acesso_id: perfilAcessoId,
      });
      if (!r.ok) {
        setErro(r.erro);
        setSalvando(false);
        return;
      }
      toast.sucesso(
        (r.ja_existia
          ? `Aluno "${form.nome}" vinculado ao usuário existente.`
          : `Aluno "${form.nome}" criado com acesso ao Portal do Aluno.`)
        + (r.email_enviado ? ' E-mail de boas-vindas enviado.' : ' Não foi possível enviar o e-mail de boas-vindas.')
      );
    }

    setSalvando(false);
    setForm(null);
    carregar();
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este aluno? (será removido também do auth)')) return;
    const { data, error } = await supabase.rpc('excluir_perfil_completo', { p_user_id: id });
    if (error || !data?.ok) {
      toast.erro('Erro ao excluir: ' + (error?.message || 'falha desconhecida'));
      return;
    }
    toast.sucesso('Aluno excluído por completo.');
    carregar();
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ color:'#1c1530', fontWeight:600, fontSize:15 }}>Alunos com acesso ao sistema</span>
        {!form && (
          <button style={sa.btnNovo} onClick={() => { setErro(''); setForm({ ...FORM_VAZIO }); }}>
            <Plus size={14}/> Novo Aluno
          </button>
        )}
      </div>

      {form && (
        <div style={sa.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={sa.titulo}>{form.id ? 'Editar Aluno' : 'Novo Aluno'}</div>
            <button style={{ background:'none', border:'none', color:'#5f5874', cursor:'pointer' }} onClick={() => setForm(null)}><X size={16}/></button>
          </div>
          {erro && <div style={{ color:'#ef4444', fontSize:12 }}>{erro}</div>}
          <div style={sa.row}>
            <Campo label="Nome *">
              <input style={sa.input} value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} />
            </Campo>
            <Campo label="E-mail *">
              <input style={sa.input} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            </Campo>
            {!form.id && (
              <Campo label="Senha *">
                <input style={{ ...sa.input, WebkitTextSecurity: 'disc' }} type="text" autoComplete="off" data-lpignore="true" value={form.senha} onChange={e => setForm({...form, senha: e.target.value})} placeholder="Senha de acesso" />
              </Campo>
            )}
          </div>
          <div style={sa.row}>
            <Campo label="Cliente *">
              <select style={sa.input} value={form.empresa || ''} onChange={e => setForm({...form, empresa: e.target.value})}>
                <option value="">Selecionar cliente</option>
                {nomesClientes.map(n => <option key={n} value={n}>{n}</option>)}
                {/* Aluno antigo com empresa que não está mais na lista de clientes */}
                {form.empresa && !nomesClientes.includes(form.empresa) && <option value={form.empresa}>{form.empresa}</option>}
              </select>
            </Campo>
            <Campo label="Perfil de Acesso">
              <select style={sa.input} value={form.perfil_acesso_id || ''} onChange={e => setForm({...form, perfil_acesso_id: e.target.value})}>
                <option value="">Sem perfil — acesso padrão de aluno</option>
                {perfisAcesso.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </Campo>
          </div>

          {/* Preview das permissões do perfil selecionado */}
          {form.perfil_acesso_id && (
            <ResumoPermissoes perfil={perfisAcesso.find(p => String(p.id) === String(form.perfil_acesso_id))} />
          )}

          <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
            <button style={{...sa.btnSalvar, opacity: salvando ? 0.6 : 1}} onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <button style={sa.btnCancelar} onClick={() => setForm(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {carregando && <div style={{ color:'#64748b', fontSize:13, textAlign:'center', padding:'1rem' }}>Carregando...</div>}

      {!carregando && alunos.length === 0 && !form && (
        <div style={{ color:'#64748b', fontSize:13, textAlign:'center', padding:'2rem' }}>Nenhum aluno cadastrado.</div>
      )}

      {alunos.map(a => {
        const perfilDoAluno = perfisAcesso.find(p => String(p.id) === String(a.perfil_acesso_id));
        return (
          <div key={a.id} style={sa.itemCard}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ color:'#1c1530', fontWeight:600, fontSize:13 }}>{a.nome}</span>
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'rgba(59,130,246,0.1)', color:'#3b82f6', border:'1px solid rgba(59,130,246,0.3)' }}>📚 Aluno</span>
                {a.empresa && (
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)' }}>
                    🏢 {a.empresa}
                  </span>
                )}
                {perfilDoAluno && (
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'rgba(139,92,246,0.1)', color:'#8b5cf6', border:'1px solid rgba(139,92,246,0.3)' }}>
                    🛡 {perfilDoAluno.nome}
                  </span>
                )}
              </div>
              <div style={{ color:'#64748b', fontSize:12, marginTop:2 }}>{a.email}</div>
              <ResumoPermissoes perfil={perfilDoAluno} />
            </div>
            <div style={{ display:'flex', gap:4, alignSelf:'flex-start' }}>
              <button style={sa.btnIcone} onClick={() => { setErro(''); setForm({...a, senha:'', perfil_acesso_id: a.perfil_acesso_id || '', empresa: a.empresa || ''}); }}><Edit2 size={13}/></button>
              <button style={{...sa.btnIcone, color:'#ef4444'}} onClick={() => excluir(a.id)}><Trash2 size={13}/></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4, flex:1, minWidth:160 }}>
      <label style={{ fontSize:11, color:'#5f5874', fontWeight:600 }}>{label}</label>
      {children}
    </div>
  );
}

export function Pessoas({ clientes, setClientes, perfisAcesso, setPerfisAcesso, isAdmin = false, onAplicarPerfis }) {
  const [aba, setAba] = useState('clientes');

  const ABAS = [
    { id: 'clientes',  label: '👥 Clientes' },
    { id: 'alunos',    label: '🎓 Alunos' },
    { id: 'usuarios',  label: '👤 Usuários do Sistema' },
    { id: 'perfis',    label: '🛡 Perfis de Acesso' },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
      <div style={s.abas}>
        {ABAS.map(a => (
          <button key={a.id} style={{...s.aba, ...(aba===a.id?s.abaAtiva:{})}} onClick={() => setAba(a.id)}>
            {a.label}
          </button>
        ))}
      </div>

      {aba === 'clientes' && <Clientes clientes={clientes} setClientes={setClientes} />}
      {aba === 'alunos'   && <AbaAlunos perfisAcesso={perfisAcesso||[]} clientes={clientes||[]} />}
      {aba === 'usuarios' && <Usuarios perfisAcesso={perfisAcesso||[]} isAdmin={isAdmin} />}
      {aba === 'perfis'   && <PerfisAcesso perfisAcesso={perfisAcesso||[]} setPerfisAcesso={setPerfisAcesso} onAplicarPerfis={onAplicarPerfis} />}
    </div>
  );
}

const s = {
  abas:     { display:'flex', gap:'0.5rem', borderBottom:'1px solid #e5e0ef', paddingBottom:'0.75rem', flexWrap:'wrap' },
  aba:      { background:'transparent', border:'1px solid #e5e0ef', borderRadius:20, color:'#5f5874', padding:'7px 18px', fontSize:13, fontWeight:500, cursor:'pointer' },
  abaAtiva: { background:'#7c3aed', border: '1px solid #7c3aed', color:'white' },
};

const sa = {
  btnNovo:    { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' },
  btnSalvar:  { background:'#7c3aed', color:'white', border:'none', borderRadius:6, padding:'8px 20px', fontSize:13, fontWeight:600, cursor:'pointer' },
  btnCancelar:{ background:'transparent', color:'#5f5874', border:'1px solid #e5e0ef', borderRadius:6, padding:'8px 20px', fontSize:13, cursor:'pointer' },
  btnIcone:   { background:'transparent', border:'none', color:'#5f5874', cursor:'pointer', padding:4 },
  card:       { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:10, padding:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  titulo:     { fontSize:13, fontWeight:600, color:'#1c1530' },
  row:        { display:'flex', gap:'0.75rem', flexWrap:'wrap' },
  input:      { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:6, color:'#1c1530', padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' },
  itemCard:   { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:8, padding:'0.75rem 1rem', display:'flex', alignItems:'flex-start', gap:'0.75rem' },
};
